import { useState, useCallback, useEffect, useRef } from 'react';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';
import { useAuthContext } from '@/context/AuthContext';
import { authApi } from '@/lib/api';
import styles from '@/styles/components/PinEntry.module.css';

const PIN_LENGTH = 4;
const MAX_ATTEMPTS = 5;
const LOCKOUT_SECONDS = 30;
const PRESET_PIN = '1975';

function hashPin(pin: string): string {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + 'yeastus-salt');
  return bytesToHex(sha256(data));
}

const PRESET_PIN_HASH = hashPin(PRESET_PIN);

export function PinEntry() {
  const { login } = useAuthContext();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockoutEnd, setLockoutEnd] = useState<number | null>(null);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Lockout countdown
  useEffect(() => {
    if (!lockoutEnd) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((lockoutEnd - Date.now()) / 1000));
      setLockoutRemaining(remaining);
      if (remaining <= 0) {
        setLockoutEnd(null);
        setAttempts(0);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutEnd]);

  // Keyboard support
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (lockoutEnd || isSubmitting) return;
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter' && pin.length === PIN_LENGTH) {
        handleSubmit();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, lockoutEnd, isSubmitting]);

  const clearError = useCallback(() => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(''), 3000);
  }, []);

  const showError = useCallback(
    (msg: string) => {
      setError(msg);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 400);
      clearError();
    },
    [clearError],
  );

  const handleDigit = useCallback(
    (digit: string) => {
      if (pin.length >= PIN_LENGTH) return;
      setPin((prev) => prev + digit);
      setError('');
    },
    [pin.length],
  );

  const handleBackspace = useCallback(() => {
    setPin((prev) => prev.slice(0, -1));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (pin.length !== PIN_LENGTH || isSubmitting) return;

    const enteredHash = hashPin(pin);

    if (enteredHash !== PRESET_PIN_HASH) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPin('');

      if (newAttempts >= MAX_ATTEMPTS) {
        setLockoutEnd(Date.now() + LOCKOUT_SECONDS * 1000);
        setLockoutRemaining(LOCKOUT_SECONDS);
        showError(`Try again in ${LOCKOUT_SECONDS} seconds`);
      } else {
        showError('Wrong PIN, try again');
      }
      return;
    }

    // PIN matches — get session token from API
    setIsSubmitting(true);
    try {
      const result = await authApi.verifyPin(pin);
      const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      login(result.token, expires);
    } catch {
      showError('Unable to connect to server. Please try again.');
      setPin('');
    } finally {
      setIsSubmitting(false);
    }
  }, [pin, isSubmitting, attempts, login, showError]);

  const isLockedOut = lockoutEnd !== null && Date.now() < lockoutEnd;

  return (
    <div className={styles.overlay} role="dialog" aria-label="PIN Entry" aria-modal="true">
      <div className={styles.content}>
        <div className={styles.branding}>
          <h1 className={styles.appTitle}>Yeastus Crust</h1>
          <p className={styles.tagline}>Crust has risen</p>
        </div>
        <h2 className={styles.heading}>Enter PIN</h2>

        {/* PIN dots */}
        <div
          className={`${styles.pinDisplay} ${isShaking ? styles.shake : ''}`}
          role="status"
          aria-label={`${pin.length} digits entered`}
        >
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <div
              key={i}
              className={`${styles.dot} ${i < pin.length ? styles.dotFilled : ''}`}
            />
          ))}
        </div>

        {/* Error / lockout area */}
        <div className={styles.errorArea}>
          {isLockedOut ? (
            <p className={styles.lockoutMessage} role="alert">
              Try again in {lockoutRemaining}s
            </p>
          ) : error ? (
            <p className={styles.errorMessage} role="alert">
              {error}
            </p>
          ) : null}
        </div>

        {/* Numeric keypad */}
        <div className={styles.keypad} role="group" aria-label="PIN keypad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key, i) => {
            if (key === '') {
              return <div key={i} className={styles.keyEmpty} />;
            }
            if (key === '⌫') {
              return (
                <button
                  key={i}
                  className={`${styles.keyButton} ${styles.keyBackspace}`}
                  onClick={handleBackspace}
                  disabled={isLockedOut || isSubmitting || pin.length === 0}
                  aria-label="Delete last digit"
                  type="button"
                >
                  ⌫
                </button>
              );
            }
            return (
              <button
                key={i}
                className={styles.keyButton}
                onClick={() => handleDigit(key)}
                disabled={isLockedOut || isSubmitting || pin.length >= PIN_LENGTH}
                aria-label={`Enter ${key}`}
                type="button"
              >
                {key}
              </button>
            );
          })}
        </div>

        {/* Submit button */}
        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={isLockedOut || isSubmitting || pin.length !== PIN_LENGTH}
          type="button"
        >
          {isSubmitting ? 'Unlocking…' : 'Unlock'}
        </button>
      </div>
    </div>
  );
}
